import { isItemAuthorityReadsFeatureEnabled } from "@/config/runtimeConfig";
import { itemAuthorityService } from "@/services/itemAuthorityService";
import { itemMediaUrlResolver } from "@/services/itemMediaUrlResolver";
import { legacyMarketplaceFeedCompatibilityService } from "@/services/legacyMarketplaceFeedCompatibilityService";

const FEED_SCOPE = Object.freeze({
  open: "open",
  org: "organisations",
  publicOrg: "organisation-public",
  follows: "follows",
});

// A card shows at most four images; the item viewer resolves the rest.
const CARD_IMAGE_LIMIT = 4;
// Photo URLs are signed by another function. A page waits this long for them
// and then renders; cards whose photos arrive later sign them themselves, and
// the resolver keeps the URLs that were still in flight.
const CARD_IMAGE_SIGNING_BUDGET_MS = 2500;
// Bounded source reads per page. A page that ends on this bound is short (or
// empty) with hasMore still true, and the next page continues.
const MAX_TIMELINE_READS = 4;
const MAX_TIMELINE_STATES = 256;
const TIMELINE_TOKEN_PREFIX = "timeline:";

export class MarketplaceFeedSourceError extends Error {
  constructor(code = "unknown") {
    super("The marketplace feed could not be loaded.");
    this.name = "MarketplaceFeedSourceError";
    this.code = code;
  }
}

function fail(code) {
  throw new MarketplaceFeedSourceError(code);
}

function sourceErrorCode(error) {
  if (error instanceof MarketplaceFeedSourceError) return error.code;
  return String(error?.code || "unknown")
    .split("/")
    .pop();
}

function mapPlacementToCard(item, images = []) {
  const listing = item.listing;
  const placementId = `authority-placement:${item.organisation.orgId}:${item.itemId}`;
  return Object.freeze({
    // One canonical item may be placed in several organisations at once. The
    // card identity therefore belongs to the placement; itemId remains the
    // stable registry identity used by item commands.
    id: placementId,
    itemId: item.itemId,
    type: item.kind,
    itemName: item.title,
    title: item.title,
    itemDescription: item.summary,
    description: item.summary,
    images: Object.freeze([...images]),
    mediaAssetIds: Object.freeze([...item.mediaAssetIds]),
    organisation: item.organisation.orgId,
    organisationName: item.organisation.name,
    organisationPublicIdentifier: item.organisation.publicIdentifier,
    publicationAudience: item.publication.audience,
    listing: listing === null ? null : Object.freeze({ ...listing }),
    priceAtomic: listing?.priceAtomic ?? null,
    currencyCode: listing?.currencyCode ?? null,
    currencySymbol: listing?.currencySymbol ?? null,
    coinSymbol: listing?.currencySymbol ?? null,
    existingStock: listing?.availableStock,
    _authorityPresentation: true,
    _originalPath: `authority-item/${item.itemId}/${item.organisation.orgId}`,
  });
}

function withinBudget(promise, budgetMs, fallback) {
  let timer;
  const expiry = new Promise((resolve) => {
    timer = setTimeout(() => resolve(fallback), budgetMs);
  });
  return Promise.race([promise.catch(() => fallback), expiry]).finally(() =>
    clearTimeout(timer),
  );
}

// Cards keep the timestamp shape the legacy feed returns, so the marketplace
// treats both kinds of entry alike.
function withTimelineTime(card, time) {
  return Object.freeze({
    ...card,
    createdAt: Object.freeze({
      _seconds: Math.floor(time / 1000),
      _nanoseconds: (time % 1000) * 1e6,
    }),
  });
}

function normalizeAuthorityOptions(options) {
  if (options.profileUserUid) fail("profile-presentation-unavailable");
  const scope = options.publicOrganisationView
    ? FEED_SCOPE.publicOrg
    : options.scopeOrganisation
    ? FEED_SCOPE.org
    : FEED_SCOPE[options.feedScope || "open"];
  if (!scope) fail("invalid-scope");
  const actorProvided =
    options.actorUid !== null && options.actorUid !== undefined;
  const hasActor =
    typeof options.actorUid === "string" && options.actorUid.trim().length > 0;
  if (
    (actorProvided && !hasActor) ||
    (![FEED_SCOPE.open, FEED_SCOPE.publicOrg].includes(scope) && !hasActor)
  ) {
    fail("unauthenticated");
  }
  const orgId = [FEED_SCOPE.org, FEED_SCOPE.publicOrg].includes(scope)
    ? options.organisationUid
    : null;
  if ([FEED_SCOPE.org, FEED_SCOPE.publicOrg].includes(scope) && !orgId) {
    fail("organisation-required");
  }
  return Object.freeze({
    scope,
    orgId,
    cursor: options.lastDoc ?? null,
    // Both feed backends accept this bound (legacy compatibility caps at 24).
    limit: Math.min(24, Math.max(1,
      Number.isSafeInteger(options.batchSize) ? options.batchSize : 12)),
  });
}

// --- Chronological timeline -------------------------------------------------
//
// While item reads are on, the marketplace is one timeline built from two
// sources that are each already newest-first: item placements from the items
// authority (ordered by first publication) and media posts and NFTs from the
// legacy feed (ordered by creation). They are merged strictly by time. There
// is no ranking: an entry's position is its publication time and nothing else.
//
// A page can only emit an entry once the other source has shown what it holds
// at that point in time, so each source keeps a small buffer of entries it has
// read but not yet shown. That state stays in memory and the caller receives
// an opaque token for it.

function entryTime(value) {
  if (Number.isSafeInteger(value) && value >= 0) return value;
  const seconds = value?._seconds ?? value?.seconds;
  if (Number.isSafeInteger(seconds) && seconds >= 0) {
    const nanoseconds = value?._nanoseconds ?? value?.nanoseconds;
    return (
      seconds * 1000 +
      (Number.isSafeInteger(nanoseconds) ? Math.floor(nanoseconds / 1e6) : 0)
    );
  }
  return null;
}

// Each source is newest-first. An entry with a missing or out-of-order time
// keeps its place in its own source by taking the time of the entry before it.
function appendEntries(source, values, timeOf, keyOf) {
  for (const value of values) {
    const key = keyOf(value);
    if (!key || source.taken.has(key)) continue;
    source.taken.add(key);
    const time = timeOf(value);
    source.lastTime = Math.min(source.lastTime, time ?? source.lastTime);
    source.buffer.push({ key, time: source.lastTime, value });
  }
}

function initialSource(done = false) {
  return {
    cursor: null,
    done,
    buffer: [],
    // Keys already read from this source, so a restarted read (an expired
    // server cursor) never shows an entry twice.
    taken: new Set(),
    lastTime: Number.MAX_SAFE_INTEGER,
  };
}

function cloneSource(source) {
  return {
    cursor: source.cursor,
    done: source.done,
    buffer: [...source.buffer],
    taken: new Set(source.taken),
    lastTime: source.lastTime,
  };
}

const placementKey = (placement) =>
  placement?.itemId && placement?.organisation?.orgId
    ? `authority-placement:${placement.organisation.orgId}:${placement.itemId}`
    : null;

export function createMarketplaceFeedSource({
  authorityEnabled = isItemAuthorityReadsFeatureEnabled,
  authorityReader = itemAuthorityService,
  legacyReader = legacyMarketplaceFeedCompatibilityService,
  legacyMediaReader = null,
  mediaResolver = itemMediaUrlResolver,
} = {}) {
  const readLegacyMedia =
    typeof legacyMediaReader === "function"
      ? legacyMediaReader
      : legacyReader?.listMediaOnlyFeed?.bind(legacyReader);

  // token -> timeline state. A token is a position: reading it again gives the
  // same page, so states are never changed after they are stored.
  const timelineStates = new Map();
  let timelineSequence = 0;
  const storeState = (state) => {
    timelineSequence += 1;
    const token = `${TIMELINE_TOKEN_PREFIX}${timelineSequence}`;
    timelineStates.set(token, state);
    if (timelineStates.size > MAX_TIMELINE_STATES) {
      timelineStates.delete(timelineStates.keys().next().value);
    }
    return token;
  };
  const loadState = (cursor, scope) => {
    if (cursor === null) {
      return {
        authority: initialSource(),
        // The legacy feed has no Following scope.
        legacy: initialSource(scope === FEED_SCOPE.follows),
      };
    }
    if (typeof cursor !== "string" || !cursor.startsWith(TIMELINE_TOKEN_PREFIX)) {
      fail("invalid-argument");
    }
    const stored = timelineStates.get(cursor);
    // Positions live in memory. One that is no longer held starts the
    // timeline again; the marketplace drops entries it already shows.
    if (!stored) return loadState(null, scope);
    return {
      authority: cloneSource(stored.authority),
      legacy: cloneSource(stored.legacy),
    };
  };

  const toCard = async (placement) =>
    mapPlacementToCard(
      placement,
      await withinBudget(
        mediaResolver.resolveImageUrls(placement.mediaAssetIds, {
          limit: CARD_IMAGE_LIMIT,
        }),
        CARD_IMAGE_SIGNING_BUDGET_MS,
        [],
      ),
    );

  const readAuthority = async (source, request) => {
    const read = (cursor) =>
      authorityReader.listItemFeed({
        scope: request.scope,
        orgId: request.orgId,
        cursor,
        limit: request.limit,
      });
    let page;
    try {
      page = await read(source.cursor);
    } catch (error) {
      // Item cursors expire after a few minutes. Start that source again from
      // the top; entries already read are skipped by their keys.
      if (source.cursor === null || sourceErrorCode(error) !== "invalid-argument") {
        throw error;
      }
      source.cursor = null;
      page = await read(null);
    }
    appendEntries(
      source,
      page.items,
      (placement) => entryTime(placement.publication?.publishedAt),
      placementKey,
    );
    source.cursor = page.hasMore ? page.nextCursor : null;
    source.done = !page.hasMore || !page.nextCursor;
  };

  const readLegacy = async (source, request) => {
    const page = await readLegacyMedia({
      scope: request.scope,
      orgId: request.orgId,
      cursor: source.cursor,
      limit: request.limit,
    });
    appendEntries(
      source,
      page.items,
      (item) => entryTime(item.createdAt) ?? entryTime(item.lastUpdated),
      (item) => item?.id || null,
    );
    source.cursor = page.hasMore ? page.nextCursor : null;
    source.done = !page.hasMore || !page.nextCursor;
  };

  const fetchTimelinePage = async (request) => {
    const state = loadState(request.cursor, request.scope);
    if (!state.legacy.done && !readLegacyMedia) fail("feature-disabled");
    const readers = { authority: readAuthority, legacy: readLegacy };
    const unavailable = new Set();
    let firstError = null;
    const picked = [];

    for (let reads = 0; picked.length < request.limit; ) {
      const empty = Object.keys(readers).filter(
        (name) =>
          state[name].buffer.length === 0 &&
          !state[name].done &&
          !unavailable.has(name),
      );
      if (empty.length > 0) {
        if (reads >= MAX_TIMELINE_READS) break;
        reads += 1;
        const results = await Promise.allSettled(
          empty.map((name) => readers[name](state[name], request)),
        );
        results.forEach((result, index) => {
          if (result.status === "fulfilled") return;
          // A source that fails keeps its position and is read again on the
          // next page; the other source carries this page.
          unavailable.add(empty[index]);
          firstError ||= result.reason;
        });
        continue;
      }
      const heads = Object.keys(readers)
        .filter((name) => state[name].buffer.length > 0)
        .sort(
          (left, right) => state[right].buffer[0].time - state[left].buffer[0].time,
        );
      if (heads.length === 0) break;
      picked.push({ name: heads[0], entry: state[heads[0]].buffer.shift() });
    }

    if (picked.length === 0 && firstError) {
      throw new MarketplaceFeedSourceError(sourceErrorCode(firstError));
    }
    const items = await Promise.all(
      picked.map(async ({ name, entry }) =>
        name === "authority"
          ? withTimelineTime(await toCard(entry.value), entry.time)
          : entry.value,
      ),
    );
    const hasMore = ["authority", "legacy"].some(
      (name) => state[name].buffer.length > 0 || !state[name].done,
    );
    return Object.freeze({
      items: Object.freeze(items),
      lastDoc: hasMore ? storeState(state) : null,
      hasMore,
      fromTimeline: true,
      fromAuthority: true,
      fromLegacyCompatibility: request.scope !== FEED_SCOPE.follows,
      fromFollowing: request.scope === FEED_SCOPE.follows,
      revalidatePromise: null,
    });
  };

  return Object.freeze({
    async fetchPage(options = {}) {
      const request = normalizeAuthorityOptions(options);
      if (authorityEnabled) {
        try {
          return await fetchTimelinePage(request);
        } catch (error) {
          if (error instanceof MarketplaceFeedSourceError) throw error;
          throw new MarketplaceFeedSourceError(sourceErrorCode(error));
        }
      }
      let result;
      try {
        if (request.scope === FEED_SCOPE.follows) fail("feature-disabled");
        const readLegacy =
          typeof legacyReader === "function"
            ? legacyReader
            : legacyReader?.listFeed?.bind(legacyReader);
        if (!readLegacy) fail("feature-disabled");
        result = await readLegacy(request);
      } catch (error) {
        if (error instanceof MarketplaceFeedSourceError) throw error;
        throw new MarketplaceFeedSourceError(
          String(error?.code || "unknown")
            .split("/")
            .pop()
        );
      }
      return Object.freeze({
        items: Object.freeze(result.items),
        lastDoc: result.nextCursor,
        hasMore: result.hasMore,
        fromTimeline: true,
        fromAuthority: false,
        fromLegacyCompatibility: true,
        fromFollowing: false,
        revalidatePromise: null,
      });
    },
  });
}

export { mapPlacementToCard as mapAuthorityPlacementToMarketplaceCard };
